import { patchState, signalStore, withComputed, withHooks, withMethods, withProps, withState } from '@ngrx/signals';
import {
  AccessDto,
  Aenderung,
  ChangeContextDto,
  CustomerDto,
  CustomerType,
  Handelsgeschaeft,
  HandelsgeschaeftCheckAccessPostRequest,
  HandelsgeschaeftResponse,
  HandelsgeschaeftService,
  HandelsgeschaeftServiceChange,
  HandelsgeschaeftStatus,
  HandelsgeschaeftTyp,
  HandelstagService,
  InterestCalculationType,
  KundenField,
  KundenService,
  Richtung,
  SimpleHgType,
  Verarbeitungsweg,
  Zinssatz
} from 'lion-api';
import { HttpErrorResponse } from '@angular/common/http';
import { computed, inject } from '@angular/core';
import { CustomerStore, SimpleMessageService, TrackingIdService, UserStore } from '@shared/data';
import { TranslateService } from '@ngx-translate/core';
import { DecimalPipe } from '@angular/common';

interface TradeTransactionState {
  tradeTransaction: Handelsgeschaeft;
  maskDisabled: boolean;
  executeDisabled: boolean;
  accessPossible: boolean;
  processing: boolean;
  korrektur: boolean;
  storno: boolean;
  isInterestRateLoading: boolean;
  tradeTransactionIsProcessing: boolean;
  hasLinienUeberziehung: boolean;
  hasBestandUeberziehung: boolean;
  correctedMurexId: string;
  isMonthYearInput: boolean;
}

const tradeTransActionState: TradeTransactionState = {
  tradeTransaction: {},
  maskDisabled: true,
  executeDisabled: true,
  accessPossible: false,
  processing: false,
  korrektur: false,
  storno: false,
  isInterestRateLoading: false,
  tradeTransactionIsProcessing: false,
  hasLinienUeberziehung: false,
  hasBestandUeberziehung: false,
  correctedMurexId: '',
  isMonthYearInput: false
};

export const TradeTransactionStore = signalStore(
  { providedIn: 'root' },
  withProps(() => ({
    _MESSAGE_KEY: 'tradeTransaction',
    _TRADE_TRANSACTION_LOCKED_CUSTOMER: 'tradeTransactionLockedCustomer',
    _TRADE_TRANSACTION_CHANGE_CONTEXT_KEY: 'tradeTransactionChangeContext',
    _TRADE_TRANSACTION_ULTIMO_KEY: 'tradeTransactionUltimoText'
  })),
  withState<TradeTransactionState>(tradeTransActionState),
  withComputed((state) => {
    return {
      formattedSystemZinssatz: computed(() => {
        const hg = state.tradeTransaction();
        const isInterestRateLoading = state.isInterestRateLoading();

        const hasDates = hg?.startValuta != null && hg?.faelligkeit != null;
        const hasNominal = hg?.betrag != null && hg.betrag !== 0;
        const isLoading = isInterestRateLoading;
        const suppressNV = isLoading || !hasDates || !hasNominal;

        const marge = hg?.systemZinssatz?.kundenMarge;
        const einstand = hg?.systemZinssatz?.einstand;
        const zinssatzExtern = hg?.systemZinssatz?.zinssatzExtern;

        let sysInterestRate: number | null = null;
        if (hg?.systemZinssatz) {
          if (zinssatzExtern != null) {
            sysInterestRate = zinssatzExtern;
          } else if (einstand != null) {
            if (marge != null) {
              if (hg.typ?.richtung === Richtung.Aufnahme) {
                sysInterestRate = einstand + marge;
              } else {
                sysInterestRate = einstand - marge;
              }
            } else {
              sysInterestRate = einstand;
            }
          }
        }

        return {
          marge: marge != null ? marge.toFixed(3).replace('.', ',') : suppressNV ? null : 'n.v.',
          einstand: einstand != null ? einstand.toFixed(3).replace('.', ',') : suppressNV ? null : 'n.v.',
          zinssatz: sysInterestRate != null ? sysInterestRate.toFixed(3).replace('.', ',') : suppressNV ? null : 'n.v.',
          zinssatzExtern: zinssatzExtern != null ? zinssatzExtern.toFixed(3).replace('.', ',') : null
        };
      })
    };
  }),
  withMethods((tradeTransActionState) => {
    const simpleMessageService = inject(SimpleMessageService);
    const translateService = inject(TranslateService);
    const handelsgeschaeftService = inject(HandelsgeschaeftService);
    const trackingService = inject(TrackingIdService);
    const customerStore = inject(CustomerStore);
    const customerService = inject(KundenService);
    const handelsTagService = inject(HandelstagService);
    const userStore = inject(UserStore);
    const decimalPipe = inject(DecimalPipe);

    return {
      loadGeschaeftForCustomer(
        customer: CustomerDto | undefined,
        isCustomerDzBank: boolean,
        verarbeitungsWeg: Verarbeitungsweg,
        typ: HandelsgeschaeftTyp,
        retailer: string,
        isDzBankUser: boolean
      ) {
        if (customer && customer.sapGpNumber && typ.simpleHgType && Object.values(SimpleHgType).includes(typ.simpleHgType)) {
          patchState(tradeTransActionState, { processing: false });

          handelsgeschaeftService
            .handelsgeschaeftInitSapGpNummerTypVerarbeitungswegGet(
              customer.sapGpNumber,
              typ.simpleHgType,
              verarbeitungsWeg,
              trackingService.generateTrackingId(),
              typ.richtung
            )
            .subscribe({
              next: (response) => {
                if (response.handelsgeschaeft) {
                  const handelsgeschaeft = response.handelsgeschaeft;
                  handelsgeschaeft.retailer = retailer;
                  this._checkAccess(isCustomerDzBank, verarbeitungsWeg, typ, isDzBankUser, handelsgeschaeft);
                }
              },
              error: (error) => {
                console.error(error);
              }
            });
        }
      },

      updateNominalChange(nominal: number, faelligkeit: Date | undefined) {
        const tradeTransaction = {
          ...tradeTransActionState.tradeTransaction()
        };
        if (
          (tradeTransaction.faelligkeit != null || tradeTransaction.typ?.simpleHgType === SimpleHgType.Tagesgeld) &&
          !tradeTransActionState.korrektur()
        ) {
          // TODO: Aenderung ist eigentlich nur für Tagesgelder vorgesehen. Warum wird das hier gesetzt?
          tradeTransaction.latestChange = 'aenderung';
          tradeTransaction.aenderung = Aenderung.Zinsaenderung;

          simpleMessageService.clearInlineMessage(tradeTransActionState._MESSAGE_KEY);
          if (!this._isValidNominal(tradeTransaction, nominal)) {
            return;
          }

          tradeTransaction.betrag = nominal;
          tradeTransaction.faelligkeit = faelligkeit;
          tradeTransaction.retailer = customerStore.retailer()?.name;

          patchState(tradeTransActionState, {
            tradeTransaction: tradeTransaction
          });
          patchState(tradeTransActionState, { executeDisabled: true });
          patchState(tradeTransActionState, { isInterestRateLoading: true });

          simpleMessageService.clearInlineMessage(tradeTransActionState._TRADE_TRANSACTION_CHANGE_CONTEXT_KEY);
          handelsgeschaeftService.handelsgeschaeftCheckPost(trackingService.generateTrackingId(), tradeTransaction).subscribe({
            next: (response) => {
              const currentNominal = tradeTransActionState.tradeTransaction().betrag;
              if (response.handelsgeschaeft?.betrag !== undefined && response.handelsgeschaeft.betrag !== currentNominal) {
                console.warn(
                  'Discarding obsolete check response. Response betrag:',
                  response.handelsgeschaeft.betrag,
                  'current nominal in store:',
                  currentNominal
                );
                return;
              }
              if (response.changeContextDto && response.changeContextDto.length != 0) {
                const changes = response.changeContextDto;
                const changeMsgs = this._getChangeContextMsgs(changes);
                if (changeMsgs.trim() !== '') {
                  simpleMessageService.sendInlineErrorMessage(
                    tradeTransActionState._TRADE_TRANSACTION_CHANGE_CONTEXT_KEY,
                    translateService.instant(changeMsgs)
                  );
                }
              }
              const updatedTradeTransaction = {
                ...tradeTransActionState.tradeTransaction(),
                systemZinssatz: {
                  einstand: response.handelsgeschaeft?.systemZinssatz?.einstand,
                  kundenMarge: response.handelsgeschaeft?.systemZinssatz?.kundenMarge,
                  zinssatzExtern: response.handelsgeschaeft?.systemZinssatz?.zinssatzExtern,
                  betragsMarge: response.handelsgeschaeft?.systemZinssatz?.betragsMarge
                }
              };
              if (response.handelsgeschaeft) {
                if (response.handelsgeschaeft.aktuellerBestand !== undefined) {
                  updatedTradeTransaction.aktuellerBestand = response.handelsgeschaeft.aktuellerBestand;
                }
                if (response.handelsgeschaeft.neuerBestand !== undefined) {
                  updatedTradeTransaction.neuerBestand = response.handelsgeschaeft.neuerBestand;
                }
              }

              patchState(tradeTransActionState, {
                tradeTransaction: updatedTradeTransaction
              });
              patchState(tradeTransActionState, {
                isInterestRateLoading: false
              });
              this.isHandelsgeschaeftValid();
            },
            error: (error) => {
              console.error('error checking zinsgruppe', error);
              patchState(tradeTransActionState, { executeDisabled: true });
              patchState(tradeTransActionState, {
                isInterestRateLoading: false
              });
            }
          });
        } else {
          tradeTransaction.betrag = nominal;
          patchState(tradeTransActionState, {
            tradeTransaction: tradeTransaction
          });
        }
      },

      isHandelsgeschaeftValid() {
        const tradeTransaction = tradeTransActionState.tradeTransaction();
        if (tradeTransaction.status === HandelsgeschaeftStatus.Cancelled || tradeTransaction.status === HandelsgeschaeftStatus.Corrected) {
          return;
        }
        // Don't overwrite existing retailer on validation!
        if (!tradeTransaction.korrektur && !tradeTransaction.storno) {
          tradeTransaction.retailer = customerStore.retailer()?.name;
        }
        if (this._isTradetransactionExisting(tradeTransaction)) {
          handelsgeschaeftService.isHandelsgeschaeftValidPost(true, tradeTransaction).subscribe({
            next: (response) => {
              const isValid = response.isValid;
              if (isValid !== undefined) {
                simpleMessageService.clearInlineMessage(tradeTransActionState._MESSAGE_KEY);
                patchState(tradeTransActionState, {
                  executeDisabled: !isValid
                });
              }
            },
            error: (err: HttpErrorResponse) => {
              patchState(tradeTransActionState, { executeDisabled: true });
              this._handleErrorMessage(err);
            }
          });
        }
      },

      checkPost(tradeTransaction: Handelsgeschaeft) {
        if (tradeTransaction) {
          patchState(tradeTransActionState, {
            tradeTransaction: tradeTransaction
          });
          patchState(tradeTransActionState, { isInterestRateLoading: true });
          simpleMessageService.clearInlineMessage(tradeTransActionState._TRADE_TRANSACTION_CHANGE_CONTEXT_KEY);
          tradeTransaction.retailer = customerStore.retailer()?.name;

          handelsgeschaeftService.handelsgeschaeftCheckPost(trackingService.generateTrackingId(), tradeTransaction).subscribe({
            next: (response) => {
              const currentNominal = tradeTransActionState.tradeTransaction().betrag;
              const isZinsaenderung =
                response.handelsgeschaeft?.aenderung === Aenderung.Zinsaenderung || tradeTransaction.aenderung === Aenderung.Zinsaenderung;
              if (!isZinsaenderung && response.handelsgeschaeft?.betrag !== undefined && response.handelsgeschaeft.betrag !== currentNominal) {
                console.warn(
                  'Discarding obsolete check response. Response betrag:',
                  response.handelsgeschaeft.betrag,
                  'current nominal in store:',
                  currentNominal
                );
                return;
              }
              if (response.changeContextDto && response.changeContextDto.length != 0) {
                const changeContex = response.changeContextDto;
                const changeMsgs = this._getChangeContextMsgs(changeContex);

                if (changeMsgs.trim() !== '') {
                  simpleMessageService.sendInlineErrorMessage(tradeTransActionState._TRADE_TRANSACTION_CHANGE_CONTEXT_KEY, changeMsgs);
                }
              }

              const updatedTradeTransaction = {
                ...tradeTransActionState.tradeTransaction(),
                systemZinssatz: {
                  einstand: response.handelsgeschaeft?.systemZinssatz?.einstand,
                  kundenMarge: response.handelsgeschaeft?.systemZinssatz?.kundenMarge,
                  zinssatzExtern: response.handelsgeschaeft?.systemZinssatz?.zinssatzExtern,
                  betragsMarge: response.handelsgeschaeft?.systemZinssatz?.betragsMarge
                },
                zinssatz: {
                  einstand: response.handelsgeschaeft?.zinssatz?.einstand,
                  kundenMarge: response.handelsgeschaeft?.zinssatz?.kundenMarge,
                  zinssatzExtern: response.handelsgeschaeft?.zinssatz?.zinssatzExtern,
                  betragsMarge: response.handelsgeschaeft?.zinssatz?.betragsMarge
                }
              };

              if (response.handelsgeschaeft) {
                if (response.handelsgeschaeft.aktuellerBestand !== undefined) {
                  updatedTradeTransaction.aktuellerBestand = response.handelsgeschaeft.aktuellerBestand;
                }
                if (response.handelsgeschaeft.neuerBestand !== undefined) {
                  updatedTradeTransaction.neuerBestand = response.handelsgeschaeft.neuerBestand;
                }
              }

              if (updatedTradeTransaction.typ?.simpleHgType !== SimpleHgType.Tagesgeld) {
                updatedTradeTransaction.startValuta = response.handelsgeschaeft?.startValuta;

                this._checkNewTradeDay(response, updatedTradeTransaction);
                updatedTradeTransaction.faelligkeit = response.handelsgeschaeft?.faelligkeit;
              } else {
                updatedTradeTransaction.startValuta = response.handelsgeschaeft?.startValuta;

                if (updatedTradeTransaction.zinssatz.einstand == null) {
                  updatedTradeTransaction.zinssatz.einstand = response.handelsgeschaeft?.zinssatz?.einstand;
                }

                if (updatedTradeTransaction.zinssatz.kundenMarge == null) {
                  updatedTradeTransaction.zinssatz.kundenMarge = response.handelsgeschaeft?.zinssatz?.kundenMarge;
                }
              }
              patchState(tradeTransActionState, {
                tradeTransaction: updatedTradeTransaction
              });
              patchState(tradeTransActionState, {
                isInterestRateLoading: false
              });
              this.isHandelsgeschaeftValid();
            },
            error: (error) => {
              console.error('error checking zinsgruppe', error);
              patchState(tradeTransActionState, {
                isInterestRateLoading: false
              });
            }
          });
        }
      },

      /**
       * Falls Startvaluta Ultimo ist und ein Laufzeitkürzel für Monat oder Jahr eingegeben wurde,
       * wird das Zieldatum auf den letzten Tag des Monats gesetzt. Im Backend erfolgt dann eine überprüfung des
       * Handelsgeschäfts. Eventuell muss dies ins Backend verlagert werden, wenn es zu komplikationen kommt.
       * Im Firmenkundengeschäft soll kein Ultimo-Ultimo Geschäft durchgeführt werden.
       * @param handelsgeschaeft
       */
      checkIsUltimo(handelsgeschaeft: Handelsgeschaeft) {
        const hg = handelsgeschaeft;
        const startValuta = hg.startValuta;
        const faelligkeit = hg.faelligkeit;

        const startValutaIsoString = startValuta?.toISOString();
        simpleMessageService.clearInlineMessage(tradeTransActionState._TRADE_TRANSACTION_ULTIMO_KEY);
        simpleMessageService.clearInlineMessage(tradeTransActionState._TRADE_TRANSACTION_CHANGE_CONTEXT_KEY);

        const isCorporate =
          customerStore.isCorporate() ||
          userStore.isCorporateMode() ||
          customerStore.customer()?.type === CustomerType.Firmenkunde ||
          hg.kunde?.type === CustomerType.Firmenkunde ||
          tradeTransActionState.tradeTransaction()?.kunde?.type === CustomerType.Firmenkunde;

        if (isCorporate) {
          hg.startValuta = startValuta;
          hg.faelligkeit = faelligkeit;
          this.updateIsMonthYearInput(false);
          if (hg.startValuta != null && hg.faelligkeit != null) {
            this.checkPost(hg);
          }
          return;
        }

        if (startValutaIsoString != null) {
          handelsTagService.handelstageIsUltimoGet(startValutaIsoString).subscribe({
            next: (isUltimo) => {
              if (isUltimo) {
                if (faelligkeit != null && tradeTransActionState.isMonthYearInput()) {
                  const msg = translateService.instant('tradingBusiness.changeContexMessages.FAELLIGKEIT_ULTIMO');
                  simpleMessageService.sendInlineWarnMessage(tradeTransActionState._TRADE_TRANSACTION_ULTIMO_KEY, msg);

                  const year = faelligkeit.getFullYear();
                  const month = faelligkeit.getMonth();
                  hg.faelligkeit = new Date(year, month + 1, 0);
                  hg.startValuta = startValuta;
                  this.updateIsMonthYearInput(false);
                }
              } else {
                hg.startValuta = startValuta;
                hg.faelligkeit = faelligkeit;
              }
              if (hg.startValuta != null && hg.faelligkeit != null) {
                this.checkPost(hg);
              }
            }
          });
        }
      },

      _getChangeContextMsgs(changeContex: ChangeContextDto[]) {
        let changeMsgs = '';
        changeContex.forEach((change) => {
          // Ultimo wird schon anderweitig angezeigt
          if (change.change && change.change != HandelsgeschaeftServiceChange.FaelligkeitUltimo) {
            const translateChangeMsg = translateService.instant('tradingBusiness.changeContexMessages.' + change.change);
            changeMsgs += translateChangeMsg + ' <br> ';
          }
        });
        return changeMsgs;
      },

      clearTradeTransaction() {
        simpleMessageService.clearInlineMessage(tradeTransActionState._TRADE_TRANSACTION_ULTIMO_KEY);
        simpleMessageService.clearInlineMessage(tradeTransActionState._TRADE_TRANSACTION_CHANGE_CONTEXT_KEY);
        simpleMessageService.clearInlineMessage(tradeTransActionState._MESSAGE_KEY);
        simpleMessageService.clearInlineMessage(tradeTransActionState._TRADE_TRANSACTION_LOCKED_CUSTOMER);
        simpleMessageService.clearInlineMessage('dateInPastKey');
        simpleMessageService.clearInlineMessage('newBestandLt0Key');
        patchState(tradeTransActionState, {
          tradeTransaction: {},
          processing: false,
          executeDisabled: true,
          tradeTransactionIsProcessing: false,
          hasLinienUeberziehung: false,
          hasBestandUeberziehung: false
        });
      },

      updateTradeTransaction(tradeTransaction: Handelsgeschaeft | undefined) {
        if (tradeTransaction) {
          if (!tradeTransaction.korrektur && !tradeTransaction.storno) {
            tradeTransaction.retailer = customerStore.retailer()?.name;
          }
          patchState(tradeTransActionState, {
            tradeTransaction: tradeTransaction
          });
        }
      },

      updateRetailer(retailer: string) {
        const tradeTransaction = {
          ...tradeTransActionState.tradeTransaction()
        };
        tradeTransaction.retailer = retailer;
        this.isHandelsgeschaeftValid();
      },

      updateBetrag(amount: number) {
        const tradeTransaction = tradeTransActionState.tradeTransaction();
        tradeTransaction.betrag = amount;
        patchState(tradeTransActionState, {
          tradeTransaction: tradeTransaction
        });
      },

      updateZinssatzMarge(marge: number) {
        const tradeTransaction = tradeTransActionState.tradeTransaction();
        if (tradeTransaction.zinssatz) {
          tradeTransaction.zinssatz.kundenMarge = marge;
          this.isHandelsgeschaeftValid();
          patchState(tradeTransActionState, {
            tradeTransaction: tradeTransaction
          });
        }
      },

      updateZinssatzEinstand(einstand: number) {
        const tradeTransaction = tradeTransActionState.tradeTransaction();
        if (tradeTransaction.zinssatz) {
          tradeTransaction.zinssatz.einstand = einstand;
          this.isHandelsgeschaeftValid();
          patchState(tradeTransActionState, {
            tradeTransaction: tradeTransaction
          });
        }
      },

      updateComment(comment: string) {
        const tradeTransaction = tradeTransActionState.tradeTransaction();
        tradeTransaction.kommentar = comment;
        patchState(tradeTransActionState, {
          tradeTransaction: tradeTransaction
        });
      },

      updateZinsMethod(zinsMethod: InterestCalculationType) {
        const tradeTransaction = tradeTransActionState.tradeTransaction();
        tradeTransaction.zinsmethode = zinsMethod;
        patchState(tradeTransActionState, {
          tradeTransaction: tradeTransaction
        });
      },

      updateNewBalance(newBalance: number) {
        const tradeTransaction = {
          ...tradeTransActionState.tradeTransaction()
        };
        tradeTransaction.neuerBestand = newBalance;
        patchState(tradeTransActionState, {
          tradeTransaction: tradeTransaction
        });
      },

      updateFixedInterest(isFixed: boolean) {
        const tradeTransaction = tradeTransActionState.tradeTransaction();
        tradeTransaction.festerZinssatz = isFixed;
        patchState(tradeTransActionState, {
          tradeTransaction: tradeTransaction
        });
      },

      updateStartValuta(startValuta: Date | null) {
        const tradeTransaction = tradeTransActionState.tradeTransaction();
        if (tradeTransaction && customerStore.customer()?.type === CustomerType.Firmenkunde) {
          tradeTransaction.startValuta = startValuta ?? undefined;
          patchState(tradeTransActionState, {
            tradeTransaction: tradeTransaction
          });
        }
      },

      updateProcessingState(newState: boolean) {
        patchState(tradeTransActionState, { processing: newState });
      },

      updateIsMonthYearInput(newState: boolean) {
        patchState(tradeTransActionState, { isMonthYearInput: newState });
      },

      disableMask(isDisabled: boolean) {
        patchState(tradeTransActionState, { maskDisabled: isDisabled });
      },

      //TODO: überprüfen, inwiefern die Attribute korrektur und storno als Attribut des Objekts im Backend noch gebraucht werden, ansonsten weg
      updateKorrekturState(korrekturBool: boolean) {
        const tradeTransaction = tradeTransActionState.tradeTransaction();
        tradeTransaction.korrektur = korrekturBool;
        tradeTransaction.storno = !korrekturBool;
        patchState(tradeTransActionState, {
          tradeTransaction: tradeTransaction,
          korrektur: korrekturBool
        });
      },

      updateStornoState(stornoBool: boolean) {
        const tradeTransaction = tradeTransActionState.tradeTransaction();
        tradeTransaction.storno = stornoBool;
        tradeTransaction.korrektur = !stornoBool;
        patchState(tradeTransActionState, {
          tradeTransaction: tradeTransaction,
          storno: stornoBool
        });
      },

      updateCustomer(customer: CustomerDto | undefined) {
        if (customer) {
          const tradeTransaction = tradeTransActionState.tradeTransaction();
          tradeTransaction.kunde = customer;
          patchState(tradeTransActionState, {
            tradeTransaction: tradeTransaction
          });
        }
      },

      updateHandelsgeschaeftStatus(newStatus: HandelsgeschaeftStatus) {
        patchState(tradeTransActionState, {
          tradeTransaction: {
            ...tradeTransActionState.tradeTransaction(),
            status: newStatus,
            stornierbar: newStatus !== HandelsgeschaeftStatus.Cancelled
          }
        });
      },

      updateCorrectedMurexId(correctedMurexId: string) {
        patchState(tradeTransActionState, {
          correctedMurexId: correctedMurexId
        });
      },

      updateMurexId(murexId: string) {
        patchState(tradeTransActionState, {
          tradeTransaction: {
            ...tradeTransActionState.tradeTransaction(),
            murexId: murexId
          }
        });
      },

      setEffectiveInterestRate(manuelInterestRate: number) {
        const tradeTransaction = {
          ...tradeTransActionState.tradeTransaction()
        };
        const systemZinssatz = tradeTransaction.systemZinssatz;

        if (tradeTransaction.zinssatz && systemZinssatz) {
          if (tradeTransaction.zinssatz.einstand != null && tradeTransaction.typ != null) {
            this._setCustomerMarge(tradeTransaction, manuelInterestRate, tradeTransaction.zinssatz);
          } else if (systemZinssatz.einstand != null && tradeTransaction.typ != null) {
            tradeTransaction.zinssatz.einstand = systemZinssatz.einstand;
            this._setCustomerMarge(tradeTransaction, manuelInterestRate, systemZinssatz);
          }
        }
      },

      getEffectiveInterestRate(tradeTransaction: Handelsgeschaeft, isSystemInterest: boolean) {
        if (tradeTransaction.systemZinssatz && tradeTransaction.zinssatz) {
          let einstand;
          let marge;

          if (isSystemInterest) {
            einstand = tradeTransaction.systemZinssatz.einstand;
            marge = tradeTransaction.systemZinssatz.kundenMarge;
          } else {
            einstand = tradeTransaction.zinssatz.einstand;
            marge = tradeTransaction.zinssatz.kundenMarge;
          }

          if (tradeTransaction.typ) {
            if (marge != null && einstand != null) {
              if (tradeTransaction.typ.richtung == Richtung.Aufnahme) {
                return einstand + marge;
              } else {
                return einstand - marge;
              }
            } else {
              return einstand;
            }
          }
        }
        return null;
      },

      computeEffectiveInterestRate(interestRate: Zinssatz | undefined, type: HandelsgeschaeftTyp | undefined) {
        if (interestRate) {
          if (interestRate.zinssatzExtern != null) {
            return interestRate.zinssatzExtern;
          }

          if (interestRate.einstand == null || type == null) {
            return null;
          }
          if (interestRate.kundenMarge != null) {
            if (type.richtung == Richtung.Aufnahme) {
              return interestRate.einstand + interestRate.kundenMarge;
            } else {
              return interestRate.einstand - interestRate.kundenMarge;
            }
          } else {
            return interestRate.einstand;
          }
        } else return null;
      },

      /**
       * Methode, um den disbale Wert vom Button "Verbindlich ausführen" zu setzen, wenn ein Handelsgeschäft ausgeführt wird.
       * @param newState
       */
      updateTradeTransactionIsProcessing(newState: boolean) {
        patchState(tradeTransActionState, {
          tradeTransactionIsProcessing: newState
        });
      },

      updateHasLinienUeberziehung(hasLinieUberziehung: boolean) {
        patchState(tradeTransActionState, {
          hasLinienUeberziehung: hasLinieUberziehung
        });
      },

      updateHasBestandUeberziehung(hasBestandUeberziehung: boolean) {
        patchState(tradeTransActionState, {
          hasBestandUeberziehung: hasBestandUeberziehung
        });
      },

      disableExecuteButton(isDisabled: boolean) {
        patchState(tradeTransActionState, { executeDisabled: isDisabled });
      },

      _checkAccess(
        isCustomerDzBank: boolean,
        verarbeitungsWeg: Verarbeitungsweg,
        typ: HandelsgeschaeftTyp,
        isDzBankUser: boolean,
        tradeTransaction: Handelsgeschaeft
      ) {
        if (!isCustomerDzBank) {
          const handelsgeschaeft = tradeTransaction;
          if (handelsgeschaeft !== null && handelsgeschaeft.kunde !== null) {
            const kundenId = String(handelsgeschaeft.kunde?.id);
            if (kundenId) {
              const kundenTyp = userStore.isCorporateMode() ? CustomerType.Firmenkunde : CustomerType.Bankkunde;
              customerService.customerGET(kundenId, KundenField.Id, kundenTyp).subscribe({
                next: (customerDtoResponse) => {
                  handelsgeschaeft.kunde = customerDtoResponse.customer;
                  patchState(tradeTransActionState, {
                    tradeTransaction: handelsgeschaeft
                  });

                  if (!this._checkKundeZugelassen(customerDtoResponse.customer, verarbeitungsWeg, typ, isDzBankUser)) {
                    patchState(tradeTransActionState, { maskDisabled: true });
                    patchState(tradeTransActionState, {
                      executeDisabled: true
                    });
                  } else {
                    this.checkAccessPossible(handelsgeschaeft, isDzBankUser);
                  }
                }
              });
            }
          }
        } else {
          patchState(tradeTransActionState, {
            tradeTransaction: tradeTransaction
          });
          patchState(tradeTransActionState, { maskDisabled: true });
          patchState(tradeTransActionState, { executeDisabled: true });

          simpleMessageService.sendInlineWarnMessage(
            tradeTransActionState._MESSAGE_KEY,
            translateService.instant('tradingBusiness.popUpMessages.suportCustomerDzbank')
          );
        }
      },

      _checkKundeZugelassen(
        customer: CustomerDto | undefined,
        verarbeitungsWeg: Verarbeitungsweg,
        typ: HandelsgeschaeftTyp,
        isDzBankUser: boolean
      ): boolean {
        if (!customer) {
          return false;
        }

        if (customer.fusioniertMit) {
          return false;
        }

        const isAllowed = this._isAllowedForHandelsgeschaeftTypAndVerarbeitungsweg(customer, typ, verarbeitungsWeg);

        if (!isAllowed) {
          this._createMessageForUser(typ, verarbeitungsWeg, isDzBankUser, customer);
          return false;
        }

        simpleMessageService.clearInlineMessage(tradeTransActionState._TRADE_TRANSACTION_LOCKED_CUSTOMER);
        return true;
      },

      _createMessageForUser(typ: HandelsgeschaeftTyp, verarbeitungsWeg: Verarbeitungsweg, isDzBankUser: boolean, customer: CustomerDto) {
        let popUpMsg = 'tradingBusiness.popUpMessages';
        const tradeTransaction = tradeTransActionState.tradeTransaction();

        if (tradeTransaction.typ?.richtung === HandelsgeschaeftTyp.RichtungEnum.Aufnahme && typ.simpleHgType === SimpleHgType.Refi) {
          popUpMsg = popUpMsg + '.AUFNAHME_REFI';
        } else {
          popUpMsg = popUpMsg + '.processingWay' + verarbeitungsWeg;
        }

        popUpMsg = popUpMsg + 'direction' + typ.richtung + 'notAllowed';

        if (!isDzBankUser) {
          popUpMsg = popUpMsg + 'Vr';
        }

        simpleMessageService.sendInlineWarnMessage(
          tradeTransActionState._TRADE_TRANSACTION_LOCKED_CUSTOMER,
          translateService.instant(popUpMsg, { customer: customer.name })
        );
      },

      _isAllowedForHandelsgeschaeftTypAndVerarbeitungsweg(customer: CustomerDto, typ: HandelsgeschaeftTyp, verarbeitungsweg: Verarbeitungsweg) {
        if (customer.type === CustomerType.Firmenkunde) {
          switch (verarbeitungsweg) {
            case 'A':
              if (Richtung.Anlage == typ.richtung) {
                return !customer.sperreAnlage;
              }
              return false;
            default:
              return false;
          }
        }

        if (customer.type === CustomerType.Bankkunde) {
          if (typ.simpleHgType === SimpleHgType.Refi) {
            return !customer.sperreRefi;
          }
          switch (verarbeitungsweg) {
            case 'A':
              if (Richtung.Anlage == typ.richtung) {
                return !customer.sperreAnlage;
              } else {
                return !customer.sperreAufnahme;
              }
            case 'B':
              return customer.kundengruppeB !== null && !customer.sperreLcr100;

            default:
              return false;
          }
        }

        return false;
      },

      checkAccessPossible(handelsgeschaeft: Handelsgeschaeft, isDZBankUser: boolean) {
        const isStorno = handelsgeschaeft != null && handelsgeschaeft.storno;
        if (isStorno != null) {
          const body: HandelsgeschaeftCheckAccessPostRequest = {
            kunde: handelsgeschaeft.kunde,
            typ: handelsgeschaeft.typ
          };

          handelsgeschaeftService.handelsgeschaeftCheckAccessPost(isStorno, body).subscribe({
            next: (value) => {
              const access = value.access;
              const accessPossible = this._setUpAccessPossible(access, isDZBankUser);
              if (accessPossible && !(handelsgeschaeft.korrektur || handelsgeschaeft.storno)) {
                this.isHandelsgeschaeftValid();
              }
              patchState(tradeTransActionState, {
                accessPossible: accessPossible
              });
            }
          });
        }
      },

      _setUpAccessPossible(access: AccessDto | undefined, isDZBankUser: boolean) {
        let accessPossible = true;
        if (access != null) {
          accessPossible = [
            access === AccessDto.MargentableauLockedManual,
            access === AccessDto.MargentableauNotPresentManual,
            access === AccessDto.AufnahmeLockedManual,
            access === AccessDto.AnlageLockedManual
          ].includes(true);

          this._displayLockedMessage(access);

          patchState(tradeTransActionState, { maskDisabled: true });

          if (isDZBankUser) {
            patchState(tradeTransActionState, {
              maskDisabled: !accessPossible
            });
          }
          patchState(tradeTransActionState, { executeDisabled: true });
        } else {
          patchState(tradeTransActionState, { maskDisabled: false });
        }
        return accessPossible;
      },

      _displayLockedMessage(access: AccessDto) {
        if (access !== AccessDto.AufnahmeLockedManual && access !== AccessDto.AnlageLockedManual) {
          simpleMessageService.sendInlineWarnMessage(
            tradeTransActionState._MESSAGE_KEY,
            translateService.instant(`tradingBusiness.popUpMessages.${access}`)
          );
        }
      },

      _isTradetransactionExisting(tradeTransaction: Handelsgeschaeft) {
        return tradeTransaction != null && tradeTransaction.typ != null && tradeTransaction.verarbeitungsweg != null;
      },

      _setCustomerMarge(tradeTransaction: Handelsgeschaeft, manuelInterestRate: number, referenceInterestRate: Zinssatz) {
        if (tradeTransaction.typ && tradeTransaction.zinssatz && referenceInterestRate.einstand) {
          if (tradeTransaction.typ.richtung === Richtung.Anlage) {
            const result = manuelInterestRate - referenceInterestRate.einstand;
            tradeTransaction.zinssatz.kundenMarge = -result;
            patchState(tradeTransActionState, {
              tradeTransaction: tradeTransaction
            });
          } else {
            tradeTransaction.zinssatz.kundenMarge = manuelInterestRate - referenceInterestRate.einstand;
            patchState(tradeTransActionState, {
              tradeTransaction: tradeTransaction
            });
          }
        }
      },

      _checkNewTradeDay(response: HandelsgeschaeftResponse, tradeTransaction: Handelsgeschaeft) {
        const responseFaelligkeit = response.handelsgeschaeft?.faelligkeit;
        if (responseFaelligkeit) {
          const responseFaelligkeitDate = new Date(responseFaelligkeit);
          const currentFaelligkeitDate = tradeTransaction.faelligkeit ? new Date(tradeTransaction.faelligkeit) : null;

          if (currentFaelligkeitDate?.getTime() !== responseFaelligkeitDate.getTime()) {
            simpleMessageService.sendInlineInfoMessage(
              tradeTransActionState._MESSAGE_KEY,
              translateService.instant('tradingBusiness.popUpMessages.faelligkeitLater')
            );
          }
        }
      },
      /**
       * Methode, die anhand des Keys die entsprechende Nachricht auf der UI anzeigt.
       * @param err
       */
      _handleErrorMessage(err: HttpErrorResponse) {
        const messagePrefix = 'tradingBusiness.popUpMessages.';
        const errorKey = err.error.errors[0].key;

        switch (errorKey) {
          case 'neuerBestandErrorMin':
          case 'neuerBestandErrorMax': {
            const message: string = err.error.errors[0].message;
            const msgValue = this._formatErrorNumberMsg(message);
            const bestandParam = errorKey === 'neuerBestandErrorMin' ? 'minBestand' : 'maxBestand';
            const msg = translateService.instant(messagePrefix + errorKey, {
              [bestandParam]: msgValue
            });
            simpleMessageService.sendInlineErrorMessage(tradeTransActionState._MESSAGE_KEY, msg);
            break;
          }
          case 'betragsgrenzeZero': {
            simpleMessageService.sendInlineErrorMessage(
              tradeTransActionState._MESSAGE_KEY,
              translateService.instant('tradingBusiness.popUpMessages.betragsgrenzeZero')
            );
            break;
          }
          case 'nominalIncrement': {
            const zinsgruppe = tradeTransActionState.tradeTransaction.kunde?.()?.kundengruppe;
            if (zinsgruppe) {
              let nominalIncrement = 1;
              if (userStore.isDzBankUser()) {
                nominalIncrement = zinsgruppe.dzNominalIncrement ?? 1;
              } else {
                nominalIncrement = zinsgruppe.extNominalIncrement ?? 1;
              }
              if (nominalIncrement === 0) {
                simpleMessageService.sendInlineErrorMessage(
                  tradeTransActionState._MESSAGE_KEY,
                  translateService.instant('tradingBusiness.popUpMessages.betragsgrenzeZero')
                );
              } else {
                simpleMessageService.sendInlineErrorMessage(
                  tradeTransActionState._MESSAGE_KEY,
                  translateService.instant('shared.nominalIncrement', {
                    value: decimalPipe.transform(nominalIncrement, '1.2-2')
                  })
                );
              }
            }
            break;
          }
          default: {
            const msg = translateService.instant(messagePrefix + errorKey);
            simpleMessageService.sendInlineErrorMessage(tradeTransActionState._MESSAGE_KEY, msg);
          }
        }
      },

      /**
       * Methode dient dazu message nachrichten, die aus nummerischen Werten bestehen zu formatieren.
       * @param numberErrMsg
       */
      _formatErrorNumberMsg(numberErrMsg: string) {
        const msgNumberValue = parseFloat(numberErrMsg);
        if (!isNaN(msgNumberValue)) {
          return msgNumberValue.toLocaleString('de-DE', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
          });
        } else {
          return '';
        }
      },

      isValidNominal(geschaeft: Handelsgeschaeft, nominal: number) {
        return this._isValidNominal(geschaeft, nominal);
      },

      _isValidNominal(geschaeft: Handelsgeschaeft, nominal: number) {
        if (!geschaeft || !nominal) {
          return false;
        }

        const customer = geschaeft.kunde;
        if (!customer) {
          return false;
        }

        const zinsgruppe = customer.kundengruppe;
        if (!zinsgruppe) {
          return false;
        }

        let nominalIncrement = 1;
        if (userStore.isDzBankUser()) {
          nominalIncrement = zinsgruppe.dzNominalIncrement ?? 1;
        } else {
          nominalIncrement = zinsgruppe.extNominalIncrement ?? 1;
        }

        if (nominalIncrement === 0) {
          simpleMessageService.sendInlineErrorMessage(tradeTransActionState._MESSAGE_KEY, translateService.instant('shared.betragsgrenzeZero'));
          return false;
        }

        if (nominalIncrement < 0) {
          return false;
        }

        const isIncrement = nominal % nominalIncrement === 0;

        if (!isIncrement) {
          simpleMessageService.sendInlineErrorMessage(
            tradeTransActionState._MESSAGE_KEY,
            translateService.instant('shared.nominalIncrement', {
              value: decimalPipe.transform(nominalIncrement, '1.2-2')
            })
          );
        }

        return isIncrement;
      }
    };
  }),
  withHooks(() => {
    return {
      onInit() {
        console.log('Trade transaction store initialized');
      },
      onDestroy() {
        console.log('Resetting trade transaction');
      }
    };
  })
);
